import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import {
  Badge,
  Banner,
  Card,
  Pager,
  PanelState,
  RangeFilter,
} from "../components/ui";
import { downloadFile } from "../lib/api";
import { date, num, relative, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { Lead, PageMeta, RangeInfo } from "../lib/types";

/**
 * ── The one page here that exists to be acted on ───────────────────────────
 * Ordered by what the person DID, not by a score: downloads first (they took
 * the picture away), then views generated (they spent a model call), then
 * hearted shades (browsing). An admin can explain any row on this list to the
 * person whose name is on it, which a weighted score would not allow.
 */
export function LeadsPage() {
  const [days, setDays] = useState(90);
  const [minRenders, setMinRenders] = useState(1);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data, error, loading, stale } = useApi<{
    range: RangeInfo;
    leads: Lead[];
    pagination: PageMeta;
  }>("/api/admin/analytics/leads", { days, minRenders, page, pageSize: 50 });

  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      // No limit: the export streams every matching row in batches. It used
      // to take the first 2,000 and stop, which is the kind of truncation
      // nobody notices until a customer they can see here is missing from it.
      await downloadFile(
        "/api/admin/analytics/leads.csv",
        `jkmaxx-leads-${days}d.csv`,
        { days, minRenders },
      );
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : "The export failed.",
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <Page
      title="Leads"
      subtitle="Customers who have done the expensive things, recently"
      actions={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            className="select"
            value={minRenders}
            onChange={(e) => {
              setMinRenders(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Minimum views generated"
          >
            <option value={1}>1+ views</option>
            <option value={3}>3+ views</option>
            <option value={5}>5+ views</option>
            <option value={10}>10+ views</option>
          </select>
          <RangeFilter
            days={days}
            onChange={(d) => {
              setDays(d);
              setPage(1);
            }}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => void exportCsv()}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      }
    >
      {/* The export is the only thing in this console that puts customer
          contact details somewhere an admin session cannot reach. Saying so
          here is cheaper than a policy nobody reads. */}
      <Banner tone="warning" title="The export carries personal data">
        The CSV contains customers' names, phone numbers, emails and pincodes in
        plain text. It leaves this console the moment it is downloaded — every
        export is recorded in the audit log against your account. Share it the
        way you would share a customer list, because that is what it is.
      </Banner>

      {exportError && <Banner tone="critical">{exportError}</Banner>}

      <Card
        title="Ranked by intent"
        note="Downloads first — someone taking a picture of their own room to a paint counter — then views generated, then hearted shades."
        aside={data ? `${num(data.pagination.total)} matching` : undefined}
      >
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.leads.length === 0}
            emptyText="Nobody meets that threshold in this window."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Pincode</th>
                    <th className="num">Downloads</th>
                    <th className="num">Views</th>
                    <th className="num">Rooms</th>
                    <th className="num">Hearted</th>
                    <th>Last active</th>
                    <th>Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.leads.map((lead) => (
                    <tr key={lead.userId}>
                      <td>
                        <Link
                          to={`/customers/${lead.userId}`}
                          className="cell-strong"
                          style={{ textDecoration: "underline" }}
                        >
                          {lead.user?.fullName ?? "Unnamed"}
                        </Link>
                        <div className="cell-mute mono">
                          {lead.user?.phone ?? lead.userId}
                        </div>
                        {lead.user?.email && (
                          <div className="cell-mute">{lead.user.email}</div>
                        )}
                      </td>
                      <td className="cell-mute">
                        {titleCase(lead.user?.profileType)}
                      </td>
                      <td className="mono cell-mute">
                        {lead.user?.pincode ?? "—"}
                      </td>
                      <td className="num">
                        {lead.downloads > 0 ? (
                          <Badge tone="good">{num(lead.downloads)}</Badge>
                        ) : (
                          <span className="cell-mute">0</span>
                        )}
                      </td>
                      <td className="num cell-strong">{num(lead.renders)}</td>
                      <td className="num">{num(lead.projects)}</td>
                      <td className="num">{num(lead.favorites)}</td>
                      <td className="cell-mute">
                        {relative(lead.lastActivity)}
                      </td>
                      <td className="cell-mute">{date(lead.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="leads"
            />
          </PanelState>
        </div>
      </Card>
    </Page>
  );
}
